import { useCallback } from 'react';
import { toast } from '@/components/ui/use-toast';
import { findMatch } from '@/utils';
import { useGenerateNewMarketsMutation } from '@/store/api/market-manage.api';

export function useBulkMarketCreation({
	eventCategoryId,
	categoriesObject,
	selectedMarketGroups,
	setSelectedMarketGroups,
	selectedMarkets,
	setSelectedMarkets,
	bulkMatchId,
	slatesData,
	bulkMarkets,
	setBulkMarkets,
}: {
	eventCategoryId: any;
	categoriesObject: any;
	selectedMarketGroups: string[];
	setSelectedMarketGroups: (updater: any) => void;
	selectedMarkets: any[];
	setSelectedMarkets: (updater: any) => void;
	bulkMatchId: any;
	slatesData: any[];
	bulkMarkets: any[];
	setBulkMarkets: (updater: any) => void;
}) {
	const handleCategoryChange = useCallback(
		(category: string) => {
			setSelectedMarketGroups((prev: string[]) => {
				if (prev.includes(category)) {
					setSelectedMarkets((prevMarkets: any[]) =>
						prevMarkets.filter((m) => m.category !== category)
					);
					return prev.filter((item) => item !== category);
				} else {
					return [...prev, category];
				}
			});
		},
		[setSelectedMarketGroups, setSelectedMarkets]
	);

	const handleMarketChange = useCallback(
		(category: string, market: string) => {
			setSelectedMarkets((prev: any[]) => {
				const existingIndex = prev.findIndex((m) => m.category === category && m.market === market);
				if (existingIndex >= 0) {
					const newMarkets = [...prev];
					newMarkets.splice(existingIndex, 1);
					return newMarkets;
				} else {
					return [...prev, { category, market }];
				}
			});
		},
		[setSelectedMarkets]
	);

	const getMarketsForPull = useCallback(() => {
		if (!eventCategoryId || !categoriesObject) return [];

		const allMarkets: string[] = [];

		selectedMarketGroups.forEach((category) => {
			const categoryMarkets = selectedMarkets.filter((m) => m.category === category);
			if (categoryMarkets.length > 0) {
				allMarkets.push(...categoryMarkets.map((m) => m.market));
			} else {
				const allCategoryMarkets = categoriesObject[category] || [];
				allMarkets.push(...allCategoryMarkets);
			}
		});

		const categoriesWithSelectedMarkets = selectedMarkets
			.filter(({ category }) => !selectedMarketGroups.includes(category))
			.reduce((acc: string[], { category }) => {
				if (!acc.includes(category)) acc.push(category);
				return acc;
			}, []);

		categoriesWithSelectedMarkets.forEach((category) => {
			const categoryMarkets = selectedMarkets.filter((m) => m.category === category);
			allMarkets.push(...categoryMarkets.map((m) => m.market));
		});

		return allMarkets;
	}, [eventCategoryId, categoriesObject, selectedMarketGroups, selectedMarkets]);

	const chosenMatchForBulkCreate = bulkMatchId && findMatch(slatesData, bulkMatchId);

	const [generateNewMarkets] = useGenerateNewMarketsMutation();

	const pullMarkets = useCallback(() => {
		if (!chosenMatchForBulkCreate?.ext_api_id || !bulkMatchId) return;
		const marketsToPull = getMarketsForPull();

		if (marketsToPull.length === 0) {
			toast({
				title: 'No markets selected',
				description: 'Please select at least one market or category',
				variant: 'destructive',
			});
			return;
		}

		generateNewMarkets({
			gameId: chosenMatchForBulkCreate?.ext_api_id,
			matchId: bulkMatchId,
			marketGroups: marketsToPull,
		});
	}, [chosenMatchForBulkCreate, bulkMatchId, getMarketsForPull, generateNewMarkets]);

	const disableSelectAllMarketsButton = !bulkMarkets.find(
		(m) => m.ext_api_market_id && !m.isSelectedToCreate
	);

	const selectAllBulkMarkets = () => {
		setBulkMarkets((prevMarkets: any[]) =>
			prevMarkets.map((market) => ({
				...market,
				isSelectedToCreate: true,
			}))
		);
	};

	const clearAllMarketsAndCategories = useCallback(() => {
		setSelectedMarketGroups([]);
		setSelectedMarkets([]);
	}, [setSelectedMarketGroups, setSelectedMarkets]);

	return {
		handleCategoryChange,
		handleMarketChange,
		getMarketsForPull,
		chosenMatchForBulkCreate,
		pullMarkets,
		disableSelectAllMarketsButton,
		selectAllBulkMarkets,
		clearAllMarketsAndCategories,
	};
}
